import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import NotificationBell from "@/components/NotificationBell";
import { MessageCircle, Settings, Users, Bell, ChevronDown, Plug, FileText, ClipboardList, BarChart3, Mail, Code, ShoppingBag, Paintbrush, LayoutDashboard, LogOut, Search, Calendar, Shield } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showServicesMenu, setShowServicesMenu] = useState(false);
  const adminMenuRef = useRef(null);
  const servicesMenuRef = useRef(null);
  const username = localStorage.getItem("username") || "User";
  const userRole = localStorage.getItem("role") || "user";
  const isAdmin = userRole === "admin";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    localStorage.removeItem("user_id");
    toast.success("Вы вышли из системы");
    navigate("/login");
  };

  const fetchUnreadMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const total = data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadMessages(total);
      }
    } catch (err) {
      console.error("Error fetching unread messages:", err);
    }
  };

  useEffect(() => {
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
        setShowAdminMenu(false);
      }
      if (servicesMenuRef.current && !servicesMenuRef.current.contains(event.target)) {
        setShowServicesMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActiveRoute = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="flex min-h-screen bg-slate-50 overflow-hidden" data-testid="dashboard-layout">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 ml-64">
        {/* Header with notifications and messages */}
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
          {/* Left side - Top menu items */}
          <div className="flex items-center gap-1">
            {/* Tasks */}
            <button
              onClick={() => navigate('/tasks')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                isActiveRoute('/tasks')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
              }`}
              data-testid="header-tasks-btn"
            >
              <ClipboardList className="w-4 h-4" />
              <span className="text-sm font-medium">Задачи</span>
            </button>

            {/* Statistics */}
            <button
              onClick={() => navigate('/statistics')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                isActiveRoute('/statistics')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
              }`}
              data-testid="header-statistics-btn"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm font-medium">Статистика</span>
            </button>

            {/* Services Dropdown */}
            <div className="relative" ref={servicesMenuRef}>
              <button
                onClick={() => setShowServicesMenu(!showServicesMenu)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                  showServicesMenu || isActiveRoute('/temp-email') || isActiveRoute('/receipts') || isActiveRoute('/certificates')
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                }`}
                data-testid="header-services-btn"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Сервисы</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showServicesMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showServicesMenu && (
                <div className="absolute left-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
                  <button
                    onClick={() => { navigate('/temp-email'); setShowServicesMenu(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${
                      isActiveRoute('/temp-email') ? 'bg-blue-50' : ''
                    }`}
                  >
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Временная почта</span>
                  </button>
                  <button
                    onClick={() => { navigate('/receipts'); setShowServicesMenu(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100 ${
                      isActiveRoute('/receipts') ? 'bg-blue-50' : ''
                    }`}
                  >
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Чеки</span>
                  </button>
                  <button
                    onClick={() => { navigate('/certificates'); setShowServicesMenu(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100 ${
                      isActiveRoute('/certificates') ? 'bg-blue-50' : ''
                    }`}
                  >
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Справки</span>
                  </button>
                  <button
                    onClick={() => { navigate('/admin/schedule'); setShowServicesMenu(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100 ${
                      isActiveRoute('/admin/schedule') ? 'bg-blue-50' : ''
                    }`}
                  >
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Рабочий график</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right side - Admin, Messages, Notifications, User */}
          <div className="flex items-center gap-2">
          {/* Admin Menu - only for admins */}
          {isAdmin && (
            <div className="relative" ref={adminMenuRef}>
              <button
                onClick={() => setShowAdminMenu(!showAdminMenu)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                  showAdminMenu 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                }`}
                data-testid="admin-menu-btn"
                title="Админ-панель"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">Админ</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAdminMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showAdminMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
                  <button
                    onClick={() => { navigate('/admin/users'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                    data-testid="admin-users-link"
                  >
                    <Users className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Пользователи</span>
                  </button>
                  <button
                    onClick={() => { navigate('/admin/personal-data'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                    data-testid="admin-personal-data-link"
                  >
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Персональные данные</span>
                  </button>
                  <button
                    onClick={() => { navigate('/admin/notifications'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                    data-testid="admin-notifications-link"
                  >
                    <Bell className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Уведомления</span>
                  </button>
                  <button
                    onClick={() => { navigate('/admin/integrations'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                    data-testid="admin-integrations-link"
                  >
                    <Plug className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Интеграции</span>
                  </button>
                  <button
                    onClick={() => { navigate('/admin/seo'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                    data-testid="admin-seo-link"
                  >
                    <Search className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">SEO Настройки</span>
                  </button>
                  <button
                    onClick={() => { navigate('/admin/proxy'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                    data-testid="admin-proxy-link"
                  >
                    <Shield className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Прокси (OZON)</span>
                  </button>
                  <div className="border-t border-slate-200 my-1"></div>
                  <div className="px-4 py-2 text-xs text-slate-400 font-medium">API</div>
                  <button
                    onClick={() => { navigate('/ozon'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                    data-testid="admin-ozon-link"
                  >
                    <ShoppingBag className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">OZON Профили</span>
                  </button>
                  <button
                    onClick={() => { navigate('/drawing'); setShowAdminMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                    data-testid="admin-drawing-link"
                  >
                    <Paintbrush className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Рисовалка</span>
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Messages Icon */}
          <button
            onClick={() => navigate('/chat')}
            className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="header-messages-btn"
            title="Сообщения"
          >
            <MessageCircle className="w-5 h-5" />
            {unreadMessages > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </span>
            )}
          </button>
          
          {/* Notifications Bell */}
          <NotificationBell />
          
          {/* User Avatar */}
          <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-200">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-700 font-semibold text-sm">
                {username.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-sm font-medium text-slate-700 hidden sm:block">{username}</span>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-1"
              title="Выйти"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
          </div>
        </header>
        <main className="main-content flex-1 p-6 overflow-auto min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
